import { useState, useEffect, useCallback, useTransition, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { getWorkerUrl } from '../utils/workerUrl';

import { getSessionState } from '../supabaseClient';

export const dispatchTelemetry = async (eventType, payload) => {
  try {
    const apiUrl = import.meta.env.VITE_AXIM_CORE_API_URL;
    if (!apiUrl) return;

    // Sanitize transaction payloads
    const sanitizedPayload = {
      tx_hash: payload.tx_hash,
      asset_pair: payload.asset_pair,
      volume: payload.volume,
      ...payload
    };

    await fetch(`${apiUrl}/api/v1/telemetry/micro-app`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Axim-Signature': import.meta.env.VITE_AXIM_INTERNAL_KEY || ''
      },
      body: JSON.stringify({
        app_id: "green-machine",
        event_type: eventType,
        data: sanitizedPayload
      })
    });
  } catch (error) {
    console.error("Telemetry dispatch failed:", error);
  }
};

export const useSystemDiagnostics = (isAuthenticated = true, pollInterval = 15000) => {

  const [diagnosticsState, setDiagnosticsState] = useState({
    isLoading: true,
    isDegraded: false,
    edgeStatus: 'checking',
    databaseStatus: 'checking',
    edgeColo: 'N/A',
    kvStatus: 'checking',
    lastCron: null,
    edgeLatencyMs: 0,
    databaseLatencyMs: 0,
    rawTelemetry: null,
    lastChecked: null,
    error: null,
  });

  const isMountedDiagnostics = useRef(true);

  const runDiagnostics = useCallback(async () => {
    const workerBase = getWorkerUrl();
    const startEdge = performance.now();
    let edgeStatus = 'offline';
    let edgeLatencyMs = 0;
    let edgeColo = 'UNKNOWN';
    let kvStatus = 'unknown';
    let lastCron = null;
    let rawTelemetry = null;

    // 1. Check Cloudflare Edge Telemetry
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 6000);
      const res = await fetch(`${workerBase}/api/telemetry`, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      clearTimeout(timeoutId);

      if (res.ok) {
        rawTelemetry = await res.json();
        edgeStatus = rawTelemetry.status === 'healthy' ? 'healthy' : 'degraded';
        edgeColo = rawTelemetry.edge?.colo || 'EDGE';
        kvStatus = rawTelemetry.services?.kv || 'unknown';
        lastCron = rawTelemetry.services?.lastCron || null;
      } else {
        edgeStatus = 'degraded';
      }
      edgeLatencyMs = Math.round(performance.now() - startEdge);
    } catch (err) {
      edgeStatus = 'offline';
      edgeLatencyMs = Math.round(performance.now() - startEdge);
    }

    // 2. Check Supabase Database
    const startDb = performance.now();
    let databaseStatus = 'offline';
    let databaseLatencyMs = 0;

    try {
      const { error } = await supabase
        .from('blockchain_transactions')
        .select('id', { count: 'exact', head: true })
        .limit(1);

      databaseLatencyMs = Math.round(performance.now() - startDb);
      databaseStatus = error ? 'degraded' : 'healthy';
    } catch {
      databaseLatencyMs = Math.round(performance.now() - startDb);
      databaseStatus = 'offline';
    }

    if (!isMountedDiagnostics.current) return;

    const isDegraded = edgeStatus !== 'healthy' || databaseStatus !== 'healthy';

    setDiagnosticsState({
      isLoading: false,
      isDegraded,
      edgeStatus,
      databaseStatus,
      edgeColo,
      kvStatus,
      lastCron,
      edgeLatencyMs,
      databaseLatencyMs,
      rawTelemetry,
      lastChecked: new Date(),
      error: null,
    });
  }, []);

  useEffect(() => {
    isMountedDiagnostics.current = true;
    runDiagnostics();
    const interval = setInterval(runDiagnostics, pollInterval);
    return () => {
      isMountedDiagnostics.current = false;
      clearInterval(interval);
    };
  }, [runDiagnostics, pollInterval]);



  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorLocal, setErrorLocal] = useState(null);
  const [isLiveSyncing, setIsLiveSyncing] = useState(false);
  const lastKnownGood = useRef(null);

  const fetchDiagnosticsNew = useCallback(async (retryCount = 0, targetEndpoint = '/api/telemetry') => {
    setIsLiveSyncing(true);
    try {
      const baseUrl = getWorkerUrl();
      const controller = new AbortController();
      const startReq = performance.now();
      const timeoutId = setTimeout(() => controller.abort(), 4000);

      const response = await fetch(`${baseUrl}${targetEndpoint}`, {
        headers: {
            'Accept': 'application/json',
            'X-Axim-Signature': import.meta.env.VITE_AXIM_INTERNAL_KEY || 'default-internal-key-replace-in-production'
        },
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Worker diagnostics returned status: ${response.status}`);
      }

      const latency = Math.round(performance.now() - startReq);
      const json = await response.json();
      json.latencyMs = latency;
      lastKnownGood.current = json;

      // Update state silently without throwing boundaries
      setData(prev => ({
        ...prev,
        ...json,
        lastChecked: new Date(),
        latencyMs: json.latencyMs,
        colo: json.colo
      }));
      setTelemetry(json);
      setErrorLocal(null);
      try {
        sessionStorage.setItem('axim_telemetry_cache', JSON.stringify({ telemetry: json, timestamp: Date.now() }));
      } catch (e) { /* ignore */ }

      setIsLiveSyncing(false);
      setLoading(false);

      return { success: true, data: json, loading: false, error: null, isStale: false, refetch: fetchDiagnosticsNew };
    } catch (err) {
      // Diagnostic polling failures must NEVER trigger global error boundaries or flash user alerts.
      // We log silently here.

      const maxAttempts = 3;
      if (retryCount < maxAttempts) {
         // dynamic fallback to /api/health if this was the last attempt?
         // Actually, if we fail /api/telemetry we just retry. The prompt says "dynamic fallback to /api/health".
         const targetEndpoint = retryCount === maxAttempts - 1 ? '/api/health' : '/api/telemetry';
         const delay = Math.min(3000 * Math.pow(2, retryCount), 15000);
         await new Promise(res => setTimeout(res, delay));
         return fetchDiagnosticsNew(retryCount + 1, targetEndpoint);
      }

      setErrorLocal(err.message);

      let cachedData = null;
      try {
        const cached = sessionStorage.getItem('axim_telemetry_cache');
        if (cached) cachedData = JSON.parse(cached);
      } catch (e) { /* ignore */ }

      let fallbackData = cachedData?.telemetry || lastKnownGood.current || {
        status: 'standby',
        edge: 'operational',
        latency: 'cached',
        timestamp: new Date().toISOString()
      };

      setData(fallbackData);
      setLoading(false);
      setIsLiveSyncing(false);

      return { data: fallbackData, loading: false, error: err.message, isStale: true, refetch: fetchDiagnosticsNew };
    }
  }, []);



  const [telemetry, setTelemetry] = useState(null);
  const [telemetryHistory, setTelemetryHistory] = useState([]);
  const [latencyMs, setLatencyMs] = useState(0);
  const [status, setStatus] = useState('Offline'); // 'Healthy', 'Degraded', 'Offline'
  const [isFetching, setIsFetching] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [, startTransition] = useTransition();

  useEffect(() => {
    let isMounted = true;
    let timeoutId = null;

    const runFetch = async () => {
      if (document.visibilityState === 'hidden') {
         timeoutId = setTimeout(runFetch, 120000); // 2 minutes backoff when hidden
         return;
      }

      if (isMounted) {
        const result = await fetchDiagnosticsNew().catch(e => console.error("Diagnostics fetch error handled:", e));
        if (isMounted) {
          setErrorCount(currentErrorCount => {
             const newCount = result?.error ? currentErrorCount + 1 : 0;
             let baseInterval = 30000;
             if (newCount === 1) baseInterval = 30000;
             else if (newCount === 2) baseInterval = 60000;
             else if (newCount > 2) baseInterval = 120000;

             const jitter = baseInterval * 0.1 * Math.random();
             const intervalTime = baseInterval + jitter;
             timeoutId = setTimeout(runFetch, intervalTime);
             return newCount;
          });
        }
      }
    };

    runFetch();

    const handleVisibilityChange = () => {
       if (document.visibilityState === 'visible') {
          clearTimeout(timeoutId);
          runFetch();
       }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchDiagnosticsNew]);

  const [emailServiceStatus, setEmailServiceStatus] = useState('idle');
  const [emailService, setEmailService] = useState({
    status: 'operational',
    lastCheckTimestamp: null,
    lastDeliveryLatency: 0
  });

  const fetchDiagnostics = useCallback(async () => {
    setIsFetching(true);
    let currentLatency = 0;
    const start = performance.now();
    let edgeSuccess = false;
    let dbSuccess = false;
    let localTelemetry = null;
    let dbLatencyMs = 0;

    try {
      // 1. Edge Worker Telemetry
      const workerUrl = getWorkerUrl();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3500);
      const edgeRes = await fetch(`${workerUrl}/api/health`, {
         headers: {
            'X-Axim-Signature': import.meta.env.VITE_AXIM_INTERNAL_KEY || 'default-internal-key-replace-in-production'
         },
         signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (edgeRes.ok) {
        const data = await edgeRes.json().catch(() => ({}));
        if (data && data.status === "ok") {
           const edgeLatency = Math.round(performance.now() - start);
           localTelemetry = {
               latencyMs: edgeLatency,
               colo: data.region || "local",
               ...data
           };
           setTelemetry(prev => ({ ...prev, ...data, colo: data.region || "local", latencyMs: edgeLatency, offline: false, _stale: false }));
           edgeSuccess = true;
        } else if (data && data.success) {
           localTelemetry = {
               latencyMs: data.latency_ms,
               colo: data.colo,
           };
           setTelemetry(prev => ({ ...prev, ...data.data, colo: data.colo, offline: false, _stale: false }));
           edgeSuccess = true;
        } else if (data && data.status && Array.isArray(data.data) && data.data.length > 0) {
          localTelemetry = data.data[0];
          setTelemetry(prev => ({ ...prev, ...data.data[0], offline: false, _stale: false }));
          edgeSuccess = true;
        } else {
          localTelemetry = data;
          setTelemetry(prev => ({ ...prev, ...data, offline: false, _stale: false }));
          edgeSuccess = true;
        }
      }
    } catch (e) {
      console.error('Edge health check failed', e);
    }

    try {
      // 2. Supabase DB Check & Latency
      const dbStart = performance.now();
      // Using an arbitrary fast query to measure latency
      const { error } = await supabase.from('api_usage_aggregates').select('count', { count: 'exact', head: true });
      dbLatencyMs = Math.round(performance.now() - dbStart);

      if (!error) {
         dbSuccess = true;
      }
    } catch (e) {
      console.error('DB health check failed', e);
    }


    currentLatency = Math.round(performance.now() - start);
    setLatencyMs(currentLatency);

    if (edgeSuccess || dbSuccess) {
      dispatchTelemetry("market.polled", { edgeLatencyMs: localTelemetry?.latencyMs || currentLatency, dbLatencyMs, timestamp: new Date().toISOString() });
    }


    const newTelemetryEvent = {
       timestamp: new Date().toISOString(),
       edgeLatencyMs: localTelemetry?.latencyMs || currentLatency,
       dbLatencyMs,
       totalLatencyMs: currentLatency,
       status: edgeSuccess && dbSuccess ? 'Healthy' : (edgeSuccess || dbSuccess ? 'Degraded Telemetry' : 'Offline')
    };

    setTelemetryHistory(prev => {
       const newHistory = [...prev, newTelemetryEvent];
       return newHistory.slice(-50); // Cap at 50 data points
    });

    if (edgeSuccess && dbSuccess) {
       setStatus('Healthy');
       sessionStorage.setItem('axim_last_healthy_telemetry', JSON.stringify({ telemetry: localTelemetry, latencyMs: currentLatency, timestamp: Date.now() }));
       setErrorCount(0);
    } else if (edgeSuccess || dbSuccess) {
       setStatus('Degraded Telemetry');
       setErrorCount(prev => prev + 1);
    } else {
       setStatus('Offline');
       setErrorCount(prev => prev + 1);

       // Fallback mock data when edge is unreachable
       if (!localTelemetry) {
           // Fallback only if we have NO prior cached state, otherwise keep the last known healthy state
           let cachedState = null;
           try {
               const cached = sessionStorage.getItem('axim_last_healthy_telemetry');
               if (cached) cachedState = JSON.parse(cached);
           } catch (e) { /* ignore */ }

           setTelemetry(prev => {
               if (prev && Object.keys(prev).length > 0 && !prev.offline) {
                  return { ...prev, offline: true, _stale: true };
               }
               if (cachedState && cachedState.telemetry) {
                  return { ...cachedState.telemetry, offline: true, _stale: true };
               }
               return {
                   ...prev,
                   status: "standby",
                   edge: "operational",
                   latency: "cached",
                   edge_version: "v2.4.0-mock",
                   environment: "local",
                   offline: true
               };
           });
       }
    }

    setIsFetching(false);
  }, []);








  // Setup realtime subscription for external telemetry streams
  useEffect(() => {
    let isMounted = true;
    let lastUpdate = 0;

    if (!isAuthenticated) return;

    const channel = supabase.channel('telemetry_stream')
      .on(
        'broadcast',
        { event: 'telemetry_update' },
        (payload) => {
          if (isMounted && payload.payload) {
            const now = Date.now();
            if (now - lastUpdate > 1000) { // Throttle to max 1 update per second
              lastUpdate = now;
              startTransition(() => {
                 // Only update if we received valid non-blocking telemetry
                 setTelemetry(prev => ({ ...prev, ...payload.payload, _source: 'realtime' }));
              });
            }
          }
        }
      )
      .subscribe();

    return () => {
      isMounted = false;
      supabase.removeChannel(channel);
    };
  }, []);


const [computeDebt, setComputeDebt] = useState(() => {
    const cached = localStorage.getItem('axim_compute_debt_cache');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.timestamp < 60000) {
          return parsed.data;
        }
      } catch (e) { /* ignore */ }
    }
    return [];
  });

  useEffect(() => {
    let isMounted = true;
    let timeoutId = null;

    const fetchComputeDebt = async () => {
      if (document.visibilityState === 'hidden') {
         timeoutId = setTimeout(fetchComputeDebt, 60000);
         return;
      }

      try {
        const { data, error } = await supabase
          .from('api_usage_logs')
          .select('satellite_app, tokens_used, compute_cost, gross_revenue');
        if (error) {
           console.error("Failed to fetch api_usage_logs", error);
        } else if (data) {

          const aggregated = {};
          let totalRevenue = 0;
          let totalComputeCost = 0;
          data.forEach(log => {
             const app = log.satellite_app || 'unknown';
             if (!aggregated[app]) aggregated[app] = { computeCost: 0, revenue: 0 };
             aggregated[app].computeCost += Number(log.compute_cost) || 0;
             aggregated[app].revenue += Number(log.gross_revenue) || 0;
             totalRevenue += Number(log.gross_revenue) || 0;
             totalComputeCost += Number(log.compute_cost) || 0;
          });
          const result = Object.keys(aggregated).map(app => ({
             app,
             computeCost: aggregated[app].computeCost,
             revenue: aggregated[app].revenue,
             ratio: aggregated[app].revenue > 0 ? (aggregated[app].computeCost / aggregated[app].revenue) : 0
          }));

          const globalEfficiencyRatio = totalComputeCost > 0 ? (totalRevenue / totalComputeCost) : 0;

          const finalResult = { apps: result, globalEfficiencyRatio };
          if (isMounted) {
            setComputeDebt(finalResult);
            localStorage.setItem('axim_compute_debt_cache', JSON.stringify({ data: finalResult, timestamp: Date.now() }));
          }

        }
      } catch (e) {
        console.error("Compute debt fetch failed", e);
      }

      if (isMounted) {
         timeoutId = setTimeout(fetchComputeDebt, 60000);
      }
    };

    fetchComputeDebt();

    const handleVisibilityChange = () => {
       if (document.visibilityState === 'visible') {
          clearTimeout(timeoutId);
          fetchComputeDebt();
       }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);



  const verifyEmailDelivery = useCallback(async () => {
    try {
      const workerUrl = getWorkerUrl();
      const start = performance.now();

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const res = await fetch(`${workerUrl}/api/email/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Axim-Signature': import.meta.env.VITE_AXIM_INTERNAL_KEY || 'default-internal-key-replace-in-production'
        },
        body: JSON.stringify({}),
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      const latency = Math.round(performance.now() - start);

      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setEmailService({
            status: 'operational',
            lastCheckTimestamp: new Date().toISOString(),
            lastDeliveryLatency: latency
          });
          return { success: true, messageId: data.messageId, timestamp: data.timestamp };
        } else {
          setEmailService(prev => ({
            ...prev,
            status: 'degraded',
            lastCheckTimestamp: new Date().toISOString()
          }));
          return { success: false, error: data.error || 'Failed to trigger test email' };
        }
      } else {
        setEmailService(prev => ({
          ...prev,
          status: 'offline',
          lastCheckTimestamp: new Date().toISOString()
        }));
        return { success: false, error: 'Server error ' + res.status };
      }
    } catch (e) {
      console.error('Failed to verify email delivery', e);
      setEmailService(prev => ({
        ...prev,
        status: 'offline',
        lastCheckTimestamp: new Date().toISOString()
      }));
      return { success: false, error: e.message };
    }
  }, []);

  const triggerTestEmail = useCallback(async (targetEmail, subject) => {
    try {
      setEmailServiceStatus('fetching');
      const workerUrl = getWorkerUrl();
      const res = await fetch(`${workerUrl}/api/notify/test-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Axim-Signature': import.meta.env.VITE_AXIM_INTERNAL_KEY || 'default-internal-key-replace-in-production'
        },
        body: JSON.stringify({ targetEmail, subject })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setEmailServiceStatus('healthy');
        return { success: true, target: data.target, timestamp: data.timestamp };
      } else {
        setEmailServiceStatus('error');
        return { success: false, error: data.error || 'Failed to trigger test email' };
      }
    } catch (e) {
      console.error('Failed to trigger test email', e);
      setEmailServiceStatus('error');
      return { success: false, error: e.message };
    }
  }, []);

  return {
    data,
    loading,
    error: errorLocal,
    isStale: !isLiveSyncing && errorLocal !== null,
    refetch: fetchDiagnosticsNew,
    // Keep others for backward compatibility if used elsewhere
    diagnosticsState, refreshDiagnosticsState: runDiagnostics,
    telemetry, telemetryHistory, latencyMs, status, isFetching,
    computeDebt, emailServiceStatus, triggerTestEmail, emailService, verifyEmailDelivery,
    diagnostics: data,
    diagnosticsLoading: loading,
    diagnosticsError: errorLocal,
    isLiveSyncing,
    refreshDiagnostics: fetchDiagnosticsNew
  };

};
